<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">🔧 Tools & API Integrations</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Definiera externa API:er som dina AI-agenter kan anropa
        </p>
      </div>
      <button
        @click="showCreateModal = true"
        class="mt-4 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center"
      >
        <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
        </svg>
        Skapa nytt Tool
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>

    <!-- Tools List -->
    <div v-else-if="tools.length > 0" class="space-y-4">
      <div
        v-for="tool in tools"
        :key="tool.tool_id"
        class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center flex-wrap gap-2 mb-2">
              <span class="text-2xl">🔵</span>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ tool.display_name }}</h3>
              <span class="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                Aktiv
              </span>
            </div>
            
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">{{ tool.description }}</p>
            
            <!-- API Details -->
            <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 mb-3 font-mono text-sm">
              <div class="flex items-center space-x-2">
                <span :class="[
                  'px-2 py-0.5 rounded text-xs font-bold',
                  tool.api_method === 'GET' ? 'bg-blue-100 text-blue-700' :
                  tool.api_method === 'POST' ? 'bg-green-100 text-green-700' :
                  tool.api_method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
                  tool.api_method === 'DELETE' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                ]">
                  {{ tool.api_method }}
                </span>
                <span class="text-gray-700 dark:text-gray-300 break-all">{{ tool.api_url }}</span>
              </div>
            </div>

            <!-- Parameters -->
            <div v-if="tool.parameters && tool.parameters.length > 0" class="mb-3">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Parametrar:</p>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="param in tool.parameters"
                  :key="param.parameter_name"
                  class="px-2 py-1 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                >
                  {{ param.parameter_name }}
                  <span class="text-gray-500">({{ param.parameter_type }})</span>
                  <span v-if="param.is_required" class="text-red-500">*</span>
                </span>
              </div>
            </div>

            <!-- Assigned Agents -->
            <div class="mb-3">
              <span class="text-xs text-gray-500 dark:text-gray-400">Tilldelad till:</span>
              <span
                v-for="agentId in tool.assigned_agent_ids"
                :key="agentId"
                class="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
              >
                {{ getAgentName(agentId) }}
              </span>
            </div>
            
            <!-- Stats -->
            <div class="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Använd {{ tool.usage_count_7d || 0 }} gånger senaste 7 dagarna</span>
              <span>•</span>
              <span>Skapad {{ formatDate(tool.created_at) }}</span>
            </div>
          </div>
          
          <!-- Actions -->
          <div class="flex items-center space-x-2 ml-4">
            <button
              @click="editTool(tool)"
              class="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
              title="Redigera"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
            <button
              @click="deleteTool(tool)"
              class="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              title="Ta bort"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
      </svg>
      <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">Inga tools ännu</h3>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Skapa ett tool för att låta dina AI-agenter anropa externa API:er.
      </p>
      <button
        @click="showCreateModal = true"
        class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
      >
        Skapa första tool
      </button>
    </div>

    <!-- Create/Edit Modal -->
    <Teleport to="body">
      <div
        v-if="showCreateModal"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="closeModal"
      >
        <div class="flex items-center justify-center min-h-screen px-4 py-8">
          <div class="fixed inset-0 bg-black bg-opacity-50"></div>
          <div class="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {{ editingTool ? 'Redigera Tool' : 'Skapa nytt Tool' }}
            </h2>
            
            <form @submit.prevent="saveTool" class="space-y-4">
              <!-- Basic Info -->
              <div class="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <h3 class="font-semibold text-gray-900 dark:text-white">📋 Grundinformation</h3>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tool-namn (tekniskt) *
                  </label>
                  <input
                    v-model="form.name"
                    type="text"
                    required
                    :disabled="!!editingTool"
                    pattern="[a-z0-9_]+"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                    placeholder="get_booking_status"
                  />
                  <p class="mt-1 text-xs text-gray-500">Endast a-z, 0-9 och underscore</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Visningsnamn *
                  </label>
                  <input
                    v-model="form.displayName"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Hämta bokningsstatus"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Beskrivning *
                  </label>
                  <textarea
                    v-model="form.description"
                    required
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="När AI ska använda detta tool, t.ex: 'Använd när kunden frågar om sin bokning'"
                  ></textarea>
                </div>
              </div>

              <!-- API Configuration -->
              <div class="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <h3 class="font-semibold text-gray-900 dark:text-white">🌐 API Konfiguration</h3>
                
                <div class="grid grid-cols-4 gap-2">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Metod *
                    </label>
                    <select
                      v-model="form.apiMethod"
                      required
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                  </div>
                  <div class="col-span-3">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      API URL *
                    </label>
                    <input
                      v-model="form.apiUrl"
                      type="url"
                      required
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                      placeholder="https://api.example.com/bookings/{id}"
                    />
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Headers (JSON)
                  </label>
                  <textarea
                    v-model="headersText"
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                    placeholder='{"Authorization": "Bearer YOUR_API_KEY"}'
                  ></textarea>
                </div>
              </div>

              <!-- Parameters -->
              <div class="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <div class="flex items-center justify-between">
                  <h3 class="font-semibold text-gray-900 dark:text-white">⚙️ Parametrar</h3>
                  <button
                    type="button"
                    @click="addParameter"
                    class="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                  >
                    + Lägg till
                  </button>
                </div>
                
                <div v-for="(param, index) in form.parameters" :key="index" class="flex gap-2 items-start p-3 bg-white dark:bg-gray-800 rounded">
                  <div class="flex-1 grid grid-cols-2 gap-2">
                    <input
                      v-model="param.name"
                      type="text"
                      placeholder="Namn (t.ex. booking_id)"
                      class="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <select
                      v-model="param.type"
                      class="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                    </select>
                    <input
                      v-model="param.description"
                      type="text"
                      placeholder="Beskrivning"
                      class="col-span-2 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <label class="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        v-model="param.isRequired"
                        type="checkbox"
                        class="mr-2"
                      />
                      Obligatorisk
                    </label>
                  </div>
                  <button
                    type="button"
                    @click="removeParameter(index)"
                    class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Agent Assignment -->
              <div class="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <h3 class="font-semibold text-gray-900 dark:text-white">🤖 Tilldela till agenter *</h3>
                <div class="space-y-2 max-h-48 overflow-y-auto">
                  <label
                    v-for="agent in agents"
                    :key="agent.id"
                    class="flex items-center space-x-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      :value="agent.id"
                      v-model="form.agentIds"
                      class="w-4 h-4"
                    />
                    <span class="text-sm text-gray-900 dark:text-white">{{ agent.name }}</span>
                  </label>
                </div>
              </div>
              
              <div class="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  @click="closeModal"
                  class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  :disabled="saving || form.agentIds.length === 0"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {{ saving ? 'Sparar...' : (editingTool ? 'Uppdatera' : 'Skapa') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { toolsService, type Tool, type ToolParameter } from '../services/tools'
import { agentsService } from '../services/agents'

interface Agent {
  id: number
  name: string
}

const loading = ref(true)
const saving = ref(false)
const showCreateModal = ref(false)
const tools = ref<Tool[]>([])
const editingTool = ref<Tool | null>(null)
const agents = ref<Agent[]>([])
const headersText = ref('{}')

const form = ref({
  name: '',
  displayName: '',
  description: '',
  apiUrl: '',
  apiMethod: 'GET',
  parameters: [] as ToolParameter[],
  agentIds: [] as number[]
})

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('sv-SE')
}

const getAgentName = (agentId: number): string => {
  const agent = agents.value.find(a => a.id === agentId)
  return agent?.name || `Agent ${agentId}`
}

const fetchTools = async () => {
  loading.value = true
  try {
    const result = await toolsService.getTools()
    if (result.success && result.data) {
      tools.value = result.data
    }
  } catch (error) {
    console.error('Failed to fetch tools:', error)
  } finally {
    loading.value = false
  }
}

const fetchAgents = async () => {
  try {
    const result = await agentsService.getAgents()
    if (Array.isArray(result) && result.length > 0) {
      agents.value = result.map((a: any) => ({
        id: Number(a.id),
        name: a.name
      }))
    }
  } catch (error) {
    console.error('Failed to fetch agents:', error)
  }
}

const addParameter = () => {
  form.value.parameters.push({
    name: '',
    type: 'string',
    description: '',
    isRequired: true
  })
}

const removeParameter = (index: number) => {
  form.value.parameters.splice(index, 1)
}

const editTool = (tool: Tool) => {
  editingTool.value = tool
  form.value = {
    name: tool.name,
    displayName: tool.display_name,
    description: tool.description,
    apiUrl: tool.api_url,
    apiMethod: tool.api_method,
    parameters: (tool.parameters || []).map(p => ({
      name: p.parameter_name || p.name,
      type: p.parameter_type || p.type,
      description: p.description,
      isRequired: p.is_required ?? p.isRequired ?? true
    })),
    agentIds: tool.assigned_agent_ids || []
  }
  headersText.value = JSON.stringify(tool.api_headers || {}, null, 2)
  showCreateModal.value = true
}

const deleteTool = async (tool: Tool) => {
  if (!confirm(`Är du säker på att du vill ta bort "${tool.display_name}" permanent? Detta kan inte ångras.`)) return
  
  try {
    const result = await toolsService.deleteTool(tool.tool_id)
    if (result.success) {
      tools.value = tools.value.filter(t => t.tool_id !== tool.tool_id)
      alert('Tool raderat permanent!')
    } else {
      alert(`Kunde inte radera tool: ${result.error || result.message || 'Okänt fel'}`)
    }
  } catch (error) {
    console.error('Failed to delete tool:', error)
    alert(`Ett fel uppstod: ${(error as Error).message}`)
  }
}

const saveTool = async () => {
  if (form.value.agentIds.length === 0) {
    alert('Välj minst en agent')
    return
  }
  
  saving.value = true
  try {
    let apiHeaders = {}
    try {
      apiHeaders = JSON.parse(headersText.value)
    } catch (e) {
      alert('Ogiltigt JSON i Headers')
      saving.value = false
      return
    }

    const toolData = {
      name: form.value.name,
      displayName: form.value.displayName,
      description: form.value.description,
      apiUrl: form.value.apiUrl,
      apiMethod: form.value.apiMethod,
      apiHeaders,
      parameters: form.value.parameters,
      agentIds: form.value.agentIds
    }

    if (editingTool.value) {
      const result = await toolsService.updateTool(editingTool.value.tool_id, toolData)
      if (result.success) {
        await fetchTools()
        closeModal()
      }
    } else {
      const result = await toolsService.createTool(toolData)
      if (result.success) {
        await fetchTools()
        closeModal()
      }
    }
  } catch (error) {
    console.error('Failed to save tool:', error)
    alert('Kunde inte spara tool')
  } finally {
    saving.value = false
  }
}

const closeModal = () => {
  showCreateModal.value = false
  editingTool.value = null
  form.value = {
    name: '',
    displayName: '',
    description: '',
    apiUrl: '',
    apiMethod: 'GET',
    parameters: [],
    agentIds: agents.value.length > 0 ? [agents.value[0].id] : []
  }
  headersText.value = '{}'
}

onMounted(() => {
  fetchAgents()
  fetchTools()
})
</script>

